/**
 * Pure pieces of `useListInsightAudience`, kept out of `asset.hook.ts` so they can be tested
 * without its UI imports.
 */

export type InsightAudienceQueryParams = {
  advertiserId?: number | null | undefined;
  // Whatever the caller's form holds; it doubles as a cache-key segment (as before).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  executionType: any;
  /**
   * Also list pixel (vendor-managed) audiences, which are REGULAR shims. Only the Template
   * Studio's AI personalized picker sends it; without it the request is exactly what it was.
   */
  includePixelManaged?: boolean;
  /**
   * Bridge ids to list even when their pixel audience is paused or its first
   * run has not completed: an edit screen's SAVED audiences (quick-campaign
   * edit). The backend lists those only through these, so a new selection can
   * never pick one.
   */
  includeIds?: number[];
};

/**
 * Only the audiences a campaign or build can use. `processing` stays for the
 * legacy Visitor Insight audiences, which never leave it; a pixel audience
 * whose first run has not completed is hidden by the backend instead (D10).
 */
const USABLE_STATUSES = JSON.stringify(["completed", "processing"]);

export const insightAudienceMetaParams = (
  channel: string,
  { includeIds, ...queryParams }: InsightAudienceQueryParams
) => ({
  ...queryParams,
  channel,
  status: USABLE_STATUSES,
  // A JSON array (the comma form `appendQueryParams` would write is rejected);
  // omitted when empty so every other caller sends exactly what it did.
  ...(includeIds?.length ? { includeIds: JSON.stringify(includeIds) } : {}),
});

/**
 * The Studio's list must not share a cache entry with the quick-campaign picker's 1TO1 list (same
 * base key), so the flag extends the key. Without the flag the key is untouched.
 */
export const insightAudienceQueryKey = (
  baseKey: unknown[],
  includePixelManaged?: boolean,
  includeIds?: number[]
) => [
  ...baseKey,
  ...(includePixelManaged ? ["includePixelManaged"] : []),
  ...(includeIds?.length ? [{ includeIds }] : []),
];
